import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaBell, FaUserCircle, FaChevronDown, FaBars, FaUser, FaPhone, FaSms, FaFileSignature, FaEye, FaCheck, FaTrash, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useAuth } from '../modules/auth/AuthContext';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead, clearNotifications } from '../services/notificationCenterService';
import { isPushSupported, subscribeToPush, getExistingSubscription } from '../services/pushService';
import SoftphoneWidget from '../modules/communications/components/SoftphoneWidget';

const HeaderContainer = styled.header`
  background-color: ${(props) => props.theme.colors.surface};
  padding: ${(props) => props.theme.spacing(2)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.textPrimary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: ${(props) => props.theme.spacing(1)};
  @media (max-width: 768px) { display: block; }
`;

const Logo = styled.div`
  color: ${(props) => props.theme.colors.primary};
  font-size: ${(props) => props.theme.typography.fontSize.large};
  font-weight: ${(props) => props.theme.typography.fontWeight.bold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 480px) {
    font-size: ${(props) => props.theme.typography.fontSize.medium};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing(2)};

  @media (max-width: 480px) {
    gap: ${(props) => props.theme.spacing(0.5)};
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.textPrimary};
  font-size: 1.5rem;
  cursor: pointer;
  position: relative;
  padding: ${(props) => props.theme.spacing(1)};
  &:hover { color: ${(props) => props.theme.colors.primary}; }
`;

const Badge = styled.span`
  position: absolute;
  top: -2px;
  right: -4px;
  background-color: #e74c3c;
  color: white;
  font-size: 11px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  padding: 0 4px;
  border-radius: 9px;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  margin-top: ${(props) => props.theme.spacing(1)};
  min-width: 200px;
  overflow: hidden;
  z-index: 1000;
`;

const DropdownItem = styled.div`
  padding: ${(props) => props.theme.spacing(2)};
  color: ${(props) => props.theme.colors.textPrimary};
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover {
    background-color: ${(props) => props.theme.colors.primary};
    color: ${(props) => props.theme.colors.black};
  }
`;

/* Notification dropdown */
const NotifDropdown = styled.div`
  position: fixed;
  top: 60px;
  right: 10px;
  width: 360px;
  max-width: calc(100vw - 20px);
  max-height: calc(100vh - 80px);
  background: ${(props) => props.theme.colors.surface};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const NotifHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid ${(props) => props.theme.colors.inputGray}33;
  h3 { margin: 0; font-size: 15px; }
`;

const NotifActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SmallButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.primary};
  cursor: pointer;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover { background: ${(props) => props.theme.colors.primary}22; }
`;

const NotifList = styled.div`
  overflow-y: auto;
  max-height: 400px;
  flex: 1;
`;

const NotifItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid ${(props) => props.theme.colors.inputGray}22;
  background: ${(props) => props.unread ? props.theme.colors.primary + '08' : 'transparent'};
  &:hover { background: ${(props) => props.theme.colors.primary}15; }
`;

const NotifIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 14px;
  color: white;
  background: ${(props) => {
    switch (props.type) {
      case 'new_lead': return '#3d5afe';
      case 'inbound_call': return '#27ae60';
      case 'inbound_sms': return '#f39c12';
      case 'estimate_signed': return '#28a745';
      case 'quote_viewed': return '#9C27B0';
      default: return '#95a5a6';
    }
  }};
`;

const NotifContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotifTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NotifMessage = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.colors.textSecondary};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NotifTime = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.colors.textSecondary};
  margin-top: 4px;
`;

const EmptyNotif = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 14px;
`;

const UnreadDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.theme.colors.primary};
  flex-shrink: 0;
  margin-top: 6px;
`;

const EnablePushButton = styled.button`
  background: ${(props) => props.theme.colors.primary};
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  &:hover { opacity: 0.85; }
`;

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

function getTypeIcon(type) {
  switch (type) {
    case 'new_lead': return <FaUser />;
    case 'inbound_call': return <FaPhone />;
    case 'inbound_sms': return <FaSms />;
    case 'estimate_signed': return <FaFileSignature />;
    case 'quote_viewed': return <FaEye />;
    case 'change_order_sent': return <FaFileSignature />;
    case 'change_order_signed': return <FaCheckCircle />;
    case 'change_order_declined': return <FaTimesCircle />;
    default: return <FaBell />;
  }
}

const Header = ({ onMenuToggle }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const notifRef = useRef(null);
  const userMenuRef = useRef(null);

  // Check push notification support and prompt if needed
  useEffect(() => {
    async function checkPush() {
      if (!isPushSupported()) return;
      if (typeof Notification === 'undefined') return;
      if (Notification.permission === 'granted') {
        const existing = await getExistingSubscription();
        if (!existing) {
          try { await subscribeToPush(); } catch (e) { /* silent */ }
        }
        return;
      }
      if (Notification.permission === 'denied') return;
      setShowPushPrompt(true);
    }
    checkPush();
  }, []);

  const handleEnablePush = async () => {
    try {
      await subscribeToPush();
      setShowPushPrompt(false);
    } catch (err) {
      console.error('Push subscription failed:', err);
      setShowPushPrompt(false);
    }
  };

  // Poll unread count
  const fetchCount = useCallback(async () => {
    try {
      const count = await getUnreadCount();
      setUnreadCount(count);
    } catch (e) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (showNotifications) {
      setLoading(true);
      getNotifications()
        .then(data => setNotifications(data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [showNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleNotifClick = async (notif) => {
    if (!notif.isRead) {
      await markAsRead(notif.id);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n));
    }
    setShowNotifications(false);
    if (notif.link) navigate(notif.link);
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const handleClear = async () => {
    await clearNotifications();
    setNotifications(prev => prev.filter(n => !n.isRead));
    fetchCount();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <HeaderContainer>
      <MenuButton onClick={onMenuToggle}>
        <FaBars />
      </MenuButton>

      <Logo>Mr. Patch CRM</Logo>

      <Actions>
        {showPushPrompt && (
          <EnablePushButton onClick={handleEnablePush}>
            <FaBell size={10} /> Enable Alerts
          </EnablePushButton>
        )}
        <SoftphoneWidget />

        <div ref={notifRef} style={{ position: 'relative' }}>
          <IconButton onClick={() => { setShowNotifications(!showNotifications); setShowUserMenu(false); }}>
            <FaBell />
            {unreadCount > 0 && <Badge>{unreadCount > 99 ? '99+' : unreadCount}</Badge>}
          </IconButton>

          {showNotifications && (
            <NotifDropdown>
              <NotifHeader>
                <h3>Notifications</h3>
                <NotifActions>
                  {unreadCount > 0 && (
                    <SmallButton onClick={handleMarkAllRead}>
                      <FaCheck size={10} /> Read all
                    </SmallButton>
                  )}
                  <SmallButton onClick={handleClear}>
                    <FaTrash size={10} /> Clear
                  </SmallButton>
                </NotifActions>
              </NotifHeader>

              <NotifList>
                {loading ? (
                  <EmptyNotif>Loading...</EmptyNotif>
                ) : notifications.length === 0 ? (
                  <EmptyNotif>No notifications</EmptyNotif>
                ) : (
                  notifications.map(notif => (
                    <NotifItem key={notif.id} unread={!notif.isRead} onClick={() => handleNotifClick(notif)}>
                      <NotifIcon type={notif.type}>
                        {getTypeIcon(notif.type)}
                      </NotifIcon>
                      <NotifContent>
                        <NotifTitle>{notif.title}</NotifTitle>
                        {notif.message && <NotifMessage>{notif.message}</NotifMessage>}
                        <NotifTime>{timeAgo(notif.createdAt)}</NotifTime>
                      </NotifContent>
                      {!notif.isRead && <UnreadDot />}
                    </NotifItem>
                  ))
                )}
              </NotifList>
            </NotifDropdown>
          )}
        </div>

        <div ref={userMenuRef} style={{ position: 'relative' }}>
          <IconButton onClick={() => { setShowUserMenu(!showUserMenu); setShowNotifications(false); }}>
            <FaUserCircle />
            <FaChevronDown style={{ fontSize: '0.8em', marginLeft: '4px' }} />
          </IconButton>

          {showUserMenu && (
            <Dropdown>
              <DropdownItem onClick={() => { navigate('/profile'); setShowUserMenu(false); }}>Profile</DropdownItem>
              <DropdownItem onClick={() => { navigate('/settings'); setShowUserMenu(false); }}>Settings</DropdownItem>
              <DropdownItem onClick={handleLogout}>Logout</DropdownItem>
            </Dropdown>
          )}
        </div>
      </Actions>
    </HeaderContainer>
  );
};

export default Header;
